import React, { useRef, useState } from "react"
import { useReactToPrint } from "react-to-print"
import { toast } from "react-toastify"
import { Enrollment } from "../../../service/enrollment"
import { assetUrlApi } from "../../../service/certificate"
import {
  FeeStickerRow,
  feeStickerTemplateApi,
  resolveFeeStickersApi,
} from "../../../service/fee-sticker"
import { resolveStickerText, stickerTextStyle } from "../../certificate/designer/stickerRender"

// Shared by every screen that prints fee stickers (Fee Sticker screen,
// formerly the "Student Slip"/"Student Slips" buttons on the Student List
// page) so the template-loading, layer-rendering, and print CSS can't drift
// apart between call sites — there is exactly one implementation.
export const useFeeStickerPrinting = () => {
  const [feeStickerStudents, setFeeStickerStudents] = useState<
    { enrollment: Enrollment; sticker: FeeStickerRow | null }[]
  >([])
  const [feeStickerTemplate, setFeeStickerTemplate] = useState<any | null>(null)
  const [feeStickerAssetUrls, setFeeStickerAssetUrls] = useState<Record<string, string>>({})
  const [feeStickerLoading, setFeeStickerLoading] = useState(false)
  const [showFeeSticker, setShowFeeSticker] = useState(false)
  const feeStickerRef = useRef<HTMLDivElement | null>(null)

  const stickerCanvas = feeStickerTemplate?.definition?.canvas || { width: 1200, height: 600 }
  const stickerLandscape = Number(stickerCanvas.width || 0) >= Number(stickerCanvas.height || 0)
  const stickerPageWidthIn = stickerLandscape ? 4 : 2
  const stickerPageHeightIn = stickerLandscape ? 2 : 4
  const stickerPageWidthPx = stickerPageWidthIn * 96
  const stickerPageHeightPx = stickerPageHeightIn * 96
  const stickerPrintHeightIn = Math.max(stickerPageHeightIn - 0.03, 0.1)
  const stickerScaleX = stickerPageWidthPx / Number(stickerCanvas.width || 1200)
  const stickerScaleY = stickerPageHeightPx / Number(stickerCanvas.height || 600)
  const stickerTextScale = Math.min(stickerScaleX, stickerScaleY)

  const handlePrintFeeSticker = useReactToPrint({
    contentRef: feeStickerRef,
    pageStyle: `
      @page { size: ${stickerPageWidthIn}in ${stickerPageHeightIn}in; margin: 0; }
      @media print {
        html, body {
          width: ${stickerPageWidthIn}in !important;
          min-width: ${stickerPageWidthIn}in !important;
          max-width: ${stickerPageWidthIn}in !important;
          height: auto !important;
          min-height: 0 !important;
          margin: 0 !important;
          padding: 0 !important;
          background: #fff !important;
          overflow: visible !important;
          line-height: 0 !important;
        }
        .fee-sticker-print-root {
          width: ${stickerPageWidthIn}in !important;
          min-width: ${stickerPageWidthIn}in !important;
          max-width: ${stickerPageWidthIn}in !important;
          height: auto !important;
          min-height: 0 !important;
          margin: 0 !important;
          padding: 0 !important;
          background: #fff !important;
          max-height: none !important;
          overflow: visible !important;
          display: block !important;
          text-align: center !important;
          font-size: 0 !important;
          line-height: 0 !important;
          -webkit-print-color-adjust: exact !important;
          print-color-adjust: exact !important;
          color-adjust: exact !important;
        }
        .fee-sticker-card {
          width: ${stickerPageWidthIn}in !important;
          min-width: ${stickerPageWidthIn}in !important;
          max-width: ${stickerPageWidthIn}in !important;
          height: ${stickerPrintHeightIn}in !important;
          min-height: ${stickerPrintHeightIn}in !important;
          max-height: ${stickerPrintHeightIn}in !important;
          display: block !important;
          margin: 0 !important;
          border: 0 !important;
          outline: 0 !important;
          box-shadow: none !important;
          padding: 0 !important;
          overflow: hidden !important;
          overflow: clip !important;
          box-sizing: border-box !important;
          vertical-align: top !important;
          font-size: initial !important;
          line-height: normal !important;
          break-inside: avoid-page !important;
          page-break-inside: avoid !important;
          -webkit-print-color-adjust: exact !important;
          print-color-adjust: exact !important;
          color-adjust: exact !important;
        }
        .fee-sticker-card,
        .fee-sticker-card * {
          -webkit-print-color-adjust: exact !important;
          print-color-adjust: exact !important;
          color-adjust: exact !important;
        }
        .fee-sticker-print-root .fee-sticker-card.bg-white.border.mb-3 {
          margin: 0 !important;
          border: 0 !important;
          outline: 0 !important;
          box-shadow: none !important;
        }
        .fee-sticker-card {
          break-before: auto !important;
          page-break-before: auto !important;
          break-after: auto !important;
          page-break-after: auto !important;
        }
        .fee-sticker-card:not(:last-child) {
          break-after: auto !important;
          page-break-after: auto !important;
        }
      }
    `,
  })

  const renderStickerLayer = (layer: any, vars: Record<string, string>, scale = 1) => {
    if (layer.type === "text") {
      const value = resolveStickerText(layer.content, vars)
      return <div style={stickerTextStyle(layer, scale, value)}>{value}</div>
    }
    if (layer.type === "dynamic-text") {
      const value = `${layer.prefix || ""}${vars[layer.field] ?? ""}${layer.suffix || ""}`
      return (
        <div style={stickerTextStyle(layer, scale, value)}>
          <span style={{ textDecoration: layer.textDecoration }}>
            {value}
          </span>
        </div>
      )
    }
    if (layer.type === "shape") {
      return (
        <div
          style={{
            width: "100%",
            height: "100%",
            background: layer.gradient?.enabled
              ? `linear-gradient(${layer.gradient.angle ?? 90}deg, ${layer.gradient.from || "#000"}, ${layer.gradient.to || "#000"})`
              : layer.fill || "transparent",
            border: layer.strokeWidth > 0 ? `${layer.strokeWidth * scale}px solid ${layer.stroke}` : "none",
            borderRadius: layer.shape === "ellipse" ? "50%" : (layer.borderRadius || 0) * scale,
            boxSizing: "border-box",
          }}
        />
      )
    }
    if (layer.type === "line") {
      return (
        <div
          style={{
            width: "100%",
            height: "100%",
            background: layer.color || "#111",
            borderRadius: layer.rounded ? "999px" : 0,
          }}
        />
      )
    }
    if (layer.type === "image") {
      const key = layer.src || vars[layer.dynamicField] || vars[layer.dynamic_field] || ""
      const url = key ? feeStickerAssetUrls[key] : ""
      return url ? (
        <img
          src={url}
          alt=""
          style={{ width: "100%", height: "100%", objectFit: layer.fit || "contain" }}
        />
      ) : null
    }
    return null
  }

  const loadFeeStickerTemplate = async () => {
    const response = await feeStickerTemplateApi()
    const template = response.data
    if (!template) throw new Error("Fee sticker template is not configured.")

    const definition = template.definition || {}
    const imageKeys = (definition.layers || [])
      .filter((layer: any) => layer.type === "image" && layer.src)
      .map((layer: any) => layer.src)
      .filter((key: string) => !feeStickerAssetUrls[key])

    if (imageKeys.length) {
      const urls = await Promise.all(
        imageKeys.map(async (key: string) => {
          try {
            const res = await assetUrlApi(key)
            return [key, res.url] as const
          } catch {
            return [key, ""] as const
          }
        })
      )
      setFeeStickerAssetUrls(prev => ({
        ...prev,
        ...Object.fromEntries(urls.filter(([, url]) => url)),
      }))
    }

    setFeeStickerTemplate(template)
    return template
  }

  // Loads (and opens the print preview modal for) fee stickers for the given
  // students. `session` scopes the fee amounts resolved per student — pass
  // whichever session the caller has currently selected/applied.
  const openFeeStickers = async (students: Enrollment[], session?: string) => {
    if (!students.length || feeStickerLoading) return

    try {
      setFeeStickerLoading(true)
      const template = await loadFeeStickerTemplate()
      const response = await resolveFeeStickersApi({
        enrollmentIds: students.map(student => student._id),
        session,
      })
      const dynamicImageKeys = new Set<string>()
      const dynamicImageFields = (template?.definition?.layers || [])
        .filter((layer: any) => layer.type === "image" && (layer.dynamicField || layer.dynamic_field))
        .map((layer: any) => layer.dynamicField || layer.dynamic_field)
      ;(response.data || []).forEach((row: any) => {
        dynamicImageFields.forEach((field: string) => {
          const key = row?.variables?.[field]
          if (key && !feeStickerAssetUrls[key]) dynamicImageKeys.add(key)
        })
      })
      if (dynamicImageKeys.size) {
        const urls = await Promise.all(
          Array.from(dynamicImageKeys).map(async (key) => {
            try {
              const res = await assetUrlApi(key)
              return [key, res.url] as const
            } catch {
              return [key, ""] as const
            }
          })
        )
        setFeeStickerAssetUrls(prev => ({
          ...prev,
          ...Object.fromEntries(urls.filter(([, url]) => url)),
        }))
      }
      const byEnrollment = new Map((response.data || []).map(row => [row.enrollmentId, row]))
      const results = students.map(enrollment => ({
        enrollment,
        sticker: byEnrollment.get(enrollment._id) || null,
      }))

      setFeeStickerStudents(results)
      setShowFeeSticker(true)
    } catch (err: any) {
      console.error("Failed to load fee stickers:", err)
      toast.error(err?.response?.data?.message || err?.message || "Failed to load fee stickers")
    } finally {
      setFeeStickerLoading(false)
    }
  }

  const feeStickerModal =
    showFeeSticker && feeStickerStudents.length > 0 ? (
      <>
        <div
          className="modal fade show d-block"
          tabIndex={-1}
          role="dialog"
          aria-modal="true"
        >
          <div className="modal-dialog modal-xl" role="document">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">Fees Stickers</h5>
                <button
                  type="button"
                  className="btn-close"
                  onClick={() => {
                    setShowFeeSticker(false)
                  }}
                />
              </div>

              <div
                ref={feeStickerRef}
                className="modal-body p-3 printable-content fee-sticker-print-root"
                style={{
                  backgroundColor: "#f5f5f5",
                  textAlign: "left",
                  maxHeight: "65vh",
                  overflowY: "auto",
                }}
              >
                {feeStickerStudents.map(({ enrollment, sticker }) => {
                  const definition = feeStickerTemplate?.definition || {}
                  const canvas = definition.canvas || { width: 1200, height: 600, background: "#ffffff" }
                  const vars = sticker?.variables || {}
                  return (
                    <div
                      key={enrollment._id}
                      className="fee-sticker-card bg-white border mb-3"
                      style={{
                        position: "relative",
                        width: stickerPageWidthPx,
                        height: stickerPageHeightPx,
                        background: canvas.background || definition.background_color || "#ffffff",
                        overflow: "hidden",
                        breakInside: "avoid",
                        pageBreakInside: "avoid",
                      }}
                    >
                      {(definition.layers || []).map((layer: any) => (
                        <div
                          key={layer.id}
                          style={{
                            position: "absolute",
                            left: (layer.x || 0) * stickerScaleX,
                            top: (layer.y || 0) * stickerScaleY,
                            width: (layer.w || 0) * stickerScaleX,
                            height: (layer.h || 0) * stickerScaleY,
                            overflow: "hidden",
                            opacity: layer.opacity ?? 1,
                            transform: layer.rotation ? `rotate(${layer.rotation}deg)` : undefined,
                            transformOrigin: layer.pivotOrigin || "center center",
                          }}
                        >
                          {renderStickerLayer(layer, vars, stickerTextScale)}
                        </div>
                      ))}
                    </div>
                  )
                })}
              </div>

              <div className="modal-footer">
                <button
                  type="button"
                  className="btn btn-danger me-2"
                  onClick={() => {
                    setShowFeeSticker(false)
                  }}
                >
                  Close
                </button>
                <button
                  type="button"
                  className="btn btn-primary"
                  onClick={handlePrintFeeSticker}
                  disabled={feeStickerLoading}
                >
                  Print
                </button>
              </div>
            </div>
          </div>
        </div>
        <div className="modal-backdrop fade show" />
      </>
    ) : null

  return { openFeeStickers, feeStickerLoading, feeStickerModal }
}
